import { useCallback, useEffect, useRef } from "react";

import { useLocation } from "react-router-dom";

import { isClientLoggingOut, logger, refreshOktaSession } from "Services/AuthService";
import { useFlags } from "Services/FeatureFlagService";

export const OKTA_SESSION_REFRESH_THROTTLE_MS = 15_000;

/**
 * Reports page navigation, clicks, and key presses to Okta as agent activity,
 * which extends the idle window and re-issues the auth cookies with the new
 * expiry.
 *
 * Activity refreshes are leading-edge throttled so an active agent sends at
 * most one request every 15 seconds. There is deliberately no polling refresh,
 * so an agent with no activity still reaches the brand's Okta idle timeout.
 */
export function useOktaSessionIdleRefresh(): void {
    const { pathname } = useLocation();
    const { oktaSessionIdleRefresh } = useFlags();
    const previousPathname = useRef(pathname);
    const lastRefreshAt = useRef<number>();

    const refreshForActivity = useCallback(
        (source: "pathname" | "click" | "keydown") => {
            if (isClientLoggingOut()) {
                return;
            }

            const now = Date.now();
            if (lastRefreshAt.current !== undefined && now - lastRefreshAt.current < OKTA_SESSION_REFRESH_THROTTLE_MS) {
                return;
            }

            lastRefreshAt.current = now;
            void refreshOktaSession().catch((error: unknown) => {
                logger.warn("Failed to refresh Okta session", { error, pathname, source });
            });
        },
        [pathname]
    );

    useEffect(() => {
        if (!oktaSessionIdleRefresh) {
            previousPathname.current = pathname;
            lastRefreshAt.current = undefined;
            return;
        }
        if (previousPathname.current === pathname) {
            return;
        }

        previousPathname.current = pathname;
        refreshForActivity("pathname");
    }, [pathname, oktaSessionIdleRefresh, refreshForActivity]);

    useEffect(() => {
        if (!oktaSessionIdleRefresh) {
            return;
        }

        const handleClick = () => refreshForActivity("click");
        const handleKeydown = () => refreshForActivity("keydown");
        document.addEventListener("click", handleClick);
        document.addEventListener("keydown", handleKeydown);

        return () => {
            document.removeEventListener("click", handleClick);
            document.removeEventListener("keydown", handleKeydown);
        };
    }, [oktaSessionIdleRefresh, refreshForActivity]);
}
